// Pure pointer-drag reducer for calendar entries. No React, no DOM: every
// transition is computed from plain {x,y} coordinates, so it can be tested
// directly without simulating real pointer events in a browser.

export type DragState =
  | { kind: 'idle' }
  | { kind: 'pressed'; entryId: string; origin: { x: number; y: number } }
  | { kind: 'dragging'; entryId: string; origin: { x: number; y: number }; current: { x: number; y: number } };

// A press followed by a release without crossing this many pixels of
// movement is a click, not a drag: it opens the entry, it does not
// reschedule it. Without a threshold, an ordinary mouse/trackpad twitch on a
// stationary click would register as a drag and silently move a real
// scheduled post to a different day.
//
// 5px is chosen to absorb that ordinary click jitter (a couple of pixels of
// wobble on a press-release is normal, even for a deliberate click) while
// still registering a genuine drag almost as soon as it starts -- the same
// ballpark browsers use before suppressing a native `click` in favor of a
// drag gesture. Lower and jitter starts misfiring as drags; higher and a
// real drag feels laggy to begin.
const MOVE_THRESHOLD_PX = 5;

const distance = (a: { x: number; y: number }, b: { x: number; y: number }): number =>
  Math.hypot(b.x - a.x, b.y - a.y);

/** A pointer went down on `entryId` at `at`. Always starts a fresh press. */
export const onPointerDown = (_state: DragState, entryId: string, at: { x: number; y: number }): DragState => ({
  kind: 'pressed',
  entryId,
  origin: at,
});

/**
 * The pointer moved to `at`. Idle is unaffected. A press becomes a drag
 * once it has moved more than `MOVE_THRESHOLD_PX` (straight-line distance,
 * not per-axis) from its origin; below that it stays pressed. An
 * in-progress drag just tracks the new position.
 */
export const onPointerMove = (state: DragState, at: { x: number; y: number }): DragState => {
  if (state.kind === 'idle') {
    return state;
  }
  if (state.kind === 'pressed') {
    if (distance(state.origin, at) > MOVE_THRESHOLD_PX) {
      return { kind: 'dragging', entryId: state.entryId, origin: state.origin, current: at };
    }
    return state;
  }
  return { ...state, current: at };
};

/**
 * The pointer was released. Releasing a drag reports the entry to drop (the
 * caller reschedules it); releasing a mere press, or releasing while idle,
 * reports no drop -- that release is a click, and the caller should open
 * the entry instead.
 */
export const onPointerUp = (state: DragState): { next: DragState; droppedEntryId: string | null } => {
  if (state.kind === 'dragging') {
    return { next: { kind: 'idle' }, droppedEntryId: state.entryId };
  }
  return { next: { kind: 'idle' }, droppedEntryId: null };
};
